import { Input, Textarea, type TextareaProps } from '@any-ui-react/core';
import { useId } from '@mantine/hooks';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { MarkdownPreview } from './MarkdownPreview';
import { MarkdownSwitcher, MarkdownTextareaMode, MarkdownTextareaModePosition } from './MarkdownSwitcher';

interface Props extends Omit<TextareaProps, 'value'> {
  id?: string;
  initialState?: MarkdownTextareaMode;
  modePosition?: MarkdownTextareaModePosition;
  useMarkdown: boolean;
  value: string | null | undefined;
}

export const MarkdownTextarea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      description,
      descriptionProps,
      disabled,
      id: manualId,
      initialState,
      label,
      labelProps,
      modePosition = MarkdownTextareaModePosition.TOP,
      required,
      useMarkdown,
      value,
      ...rest
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const autoId = useId();
    const [mode, setMode] = useState<MarkdownTextareaMode>(initialState ?? MarkdownTextareaMode.EDITOR);
    const [hasTextareaFocus, setHasTextareaFocus] = useState<boolean>(false);

    useImperativeHandle(ref, () => textareaRef.current!);

    const id = useMemo(() => manualId ?? autoId, [manualId, autoId]);

    useEffect(() => {
      if (!useMarkdown) {
        setMode(MarkdownTextareaMode.EDITOR);
      }
    }, [useMarkdown]);

    const setTextareaFocusTrue = useCallback(() => setHasTextareaFocus(true), []);
    const setTextareaFocusFalse = useCallback(() => setHasTextareaFocus(false), []);

    useEffect(() => {
      const element = textareaRef.current;
      if (element) {
        element.addEventListener('focus', setTextareaFocusTrue);
        element.addEventListener('blur', setTextareaFocusFalse);
      }

      return () => {
        if (element) {
          element.removeEventListener('focus', setTextareaFocusTrue);
          element.removeEventListener('blur', setTextareaFocusFalse);
        }
      };
    }, [setTextareaFocusFalse, setTextareaFocusTrue]);

    const isEmptyValue = useMemo(() => (value?.trim() ?? '').length === 0, [value]);

    const onClickEditor = useCallback(() => {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Move cursor to the end of the text
          textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
        }
      }, 0);
    }, []);

    return (
      <div
        className={classNames('flex', 'flex-col', {
          'flex-col-reverse': modePosition === MarkdownTextareaModePosition.BOTTOM,
        })}
      >
        {useMarkdown && (
          <div>
            {label && (
              <Input.Label {...labelProps} htmlFor={id} required={required}>
                {label}
              </Input.Label>
            )}
            {description && <Input.Description {...descriptionProps}>{description}</Input.Description>}
            <div
              className={classNames('flex', 'items-center', {
                'mt-1': modePosition === MarkdownTextareaModePosition.TOP,
                'mb-1': modePosition === MarkdownTextareaModePosition.BOTTOM,
              })}
            >
              <MarkdownSwitcher
                activeMode={mode}
                className={classNames({
                  'rounded-tl': modePosition === MarkdownTextareaModePosition.TOP,
                  'rounded-tr': modePosition === MarkdownTextareaModePosition.TOP && isEmptyValue,
                  'rounded-bl': modePosition === MarkdownTextareaModePosition.BOTTOM,
                  'rounded-br': modePosition === MarkdownTextareaModePosition.BOTTOM && isEmptyValue,
                  'border-blue-6': hasTextareaFocus,
                })}
                desiredMode={MarkdownTextareaMode.EDITOR}
                disabled={disabled}
                position={modePosition}
                onClick={newMode => {
                  setMode(newMode);
                  onClickEditor();
                }}
              />
              <MarkdownSwitcher
                activeMode={mode}
                className={classNames({
                  'rounded-tr': modePosition === MarkdownTextareaModePosition.TOP,
                  'rounded-br': modePosition === MarkdownTextareaModePosition.BOTTOM,
                  'border-l-0': !isEmptyValue,
                })}
                desiredMode={MarkdownTextareaMode.PREVIEW}
                disabled={disabled}
                hidden={isEmptyValue}
                position={modePosition}
                onClick={setMode}
              />
            </div>
          </div>
        )}
        <div>
          <Textarea
            {...rest}
            ref={textareaRef}
            autosize={true}
            className={mode === MarkdownTextareaMode.PREVIEW ? 'hidden' : ''}
            classNames={{
              input: classNames({
                'rounded-tl-none': modePosition === MarkdownTextareaModePosition.TOP,
                'rounded-bl-none': modePosition === MarkdownTextareaModePosition.BOTTOM,
                'font-mono': useMarkdown,
              }),
            }}
            description={useMarkdown ? '' : description}
            disabled={disabled}
            id={id}
            label={useMarkdown ? '' : label}
            minRows={5}
            required={required}
            value={value ?? undefined}
          />
          {useMarkdown && (
            <div
              className={classNames('overflow-auto', 'rounded', 'border', 'px-3', 'py-2', 'md:mt-0', {
                'rounded-tl-none': modePosition === MarkdownTextareaModePosition.TOP,
                'rounded-bl-none': modePosition === MarkdownTextareaModePosition.BOTTOM,
                hidden: mode === MarkdownTextareaMode.EDITOR,
              })}
            >
              <MarkdownPreview text={value ?? ''} useMarkdown={true} />
            </div>
          )}
        </div>
      </div>
    );
  },
);
